import { FAITH_PRACTICE_CODES } from './effective.js';
import { detectIngredientFlags } from './ingredients.js';
import { mergeTags } from './visibility.js';
import { FAITHS } from './vocab.js';
/** Id's van de ingebouwde geloofssjablonen uit 0024_keukens_en_labels (event_template). */
export const BUILTIN_TEMPLATE_IDS = {
    quran: '01920000-0000-7000-8000-000000000101',
    church: '01920000-0000-7000-8000-000000000102',
    shabbat: '01920000-0000-7000-8000-000000000103',
    synagogue: '01920000-0000-7000-8000-000000000104',
    mandir: '01920000-0000-7000-8000-000000000105',
};
/**
 * Golf 2 (0030_snel, oprichter #15): "Wudu doen" naast Bidden, als afspraak- en als
 * klusjessjabloon. NIET opt-in (zoals Moskeeles): zichtbaar zolang het gezin niets anders koos.
 */
export const BUILTIN_WUDU_IDS = {
    event: '01920000-0000-7000-8000-000000000c01',
    task: '0192a000-0000-7000-8000-000000000c01',
};
/** Id's van de geloofssjablonen uit 0020_faith_templates (Moskeeles, Bidden). */
export const BUILTIN_FAITH_IDS = {
    mosqueEvent: '01920000-0000-7000-8000-00000000000b',
    prayerEvent: '01920000-0000-7000-8000-00000000000c',
    prayerTask: '0192a000-0000-7000-8000-000000000026',
};
/**
 * Namen van de ingebouwde geloofssjablonen in de TAAL van wie kijkt (golf 6, oprichter 26 sep:
 * "abdest almak moet in het nederlands staan"). De namen in de database zijn Nederlands (Bidden,
 * Wudu doen, Moskeeles, Koranles); de Turkse traditienamen (Namaz, Abdest almak, Cami dersi, Kuran
 * dersi) verschijnen alleen in de Turkse app, nooit meer in een Nederlandse app van een gezin met
 * de Turkse keuken. Review g6a r1: ook Engels, Duits, Frans en Arabisch krijgen hun eigen naam
 * (anders zag een Engelse app "Wudu doen"). De ouder kan de titel altijd aanpassen.
 */
const PRAYER_NAMES = { tr: 'Namaz', en: 'Prayer', de: 'Beten', fr: 'Prière', ar: 'الصلاة' };
const WUDU_NAMES = {
    tr: 'Abdest almak',
    en: 'Wudu',
    de: 'Wudu',
    fr: 'Ablutions (wudu)',
    ar: 'الوضوء',
};
export const BUILTIN_TEMPLATE_LOCALE_NAMES = {
    [BUILTIN_FAITH_IDS.prayerEvent]: PRAYER_NAMES,
    [BUILTIN_FAITH_IDS.prayerTask]: PRAYER_NAMES,
    [BUILTIN_WUDU_IDS.event]: WUDU_NAMES,
    [BUILTIN_WUDU_IDS.task]: WUDU_NAMES,
    [BUILTIN_FAITH_IDS.mosqueEvent]: {
        tr: 'Cami dersi',
        en: 'Mosque class',
        de: 'Moscheeunterricht',
        fr: 'Cours à la mosquée',
        ar: 'درس المسجد',
    },
    [BUILTIN_TEMPLATE_IDS.quran]: {
        tr: 'Kuran dersi',
        en: 'Quran class',
        de: 'Koranunterricht',
        fr: 'Cours de Coran',
        ar: 'درس القرآن',
    },
};
/** Naam van een ingebouwd sjabloon in de taal van de app (`tr-TR` → "Abdest almak"). */
export function builtinTemplateName(id, name, locale) {
    const names = BUILTIN_TEMPLATE_LOCALE_NAMES[id];
    if (!names)
        return name;
    const lang = locale.toLowerCase().split('-')[0] ?? '';
    return names[lang] ?? name;
}
const ALL_FAITHS = FAITHS;
/**
 * Gewoontes per geloof: welke opties bij welk geloof horen (stap 1 toont ze als uitleg: "Je ziet
 * dan Bidden, Moskee(les) en Koranles"). Pictogram-id's die nog niet bestaan (quran, church,
 * shabbat, synagogue, mandir) tekent builder B; tot dan staan ze alleen hier. Bidden hoort bij elk
 * geloof, ook bij "een ander geloof"; "Geen geloof" verbergt het wel.
 */
export const FAITH_PRACTICES = FAITH_PRACTICE_CODES;
/**
 * Geloofslabels per pictogram-id (packages/ui-core art.ts); gelden in ELKE context. `prayer` en
 * `mosque` bestaan al (live sinds 0020) en blijven zichtbaar zolang het gezin niets anders koos;
 * de nieuwe geloofspictogrammen zijn opt-in (visibility.ts).
 */
export const PICTO_FAITH_TAGS = {
    // Golf 6b: elk geloofspictogram hoort ook bij een gewoonte (`practice`, zelfde code): vinkte het
    // gezin gewoontes aan en deze niet, dan verdwijnt hij uit de voorstellen.
    prayer: { faiths: ALL_FAITHS, practice: 'prayer' },
    mosque: { faiths: ['islam'], practice: 'mosque' },
    // Golf 2: Wudu doen (handen en water). Zoals Moskee: zichtbaar zolang het gezin niets anders
    // koos (bestaande huishoudens, net als Bidden), verborgen zonder Islam.
    wudu: { faiths: ['islam'], practice: 'wudu' },
    quran: { faiths: ['islam'], optIn: true, practice: 'quran' },
    church: { faiths: ['christianity'], optIn: true, practice: 'church' },
    shabbat: { faiths: ['judaism'], optIn: true, practice: 'shabbat' },
    synagogue: { faiths: ['judaism'], optIn: true, practice: 'synagogue' },
    mandir: { faiths: ['hinduism'], optIn: true, practice: 'mandir' },
    // Oprichter 1 okt: Sohbet alleen als het gezin Islam koos én Turks bij keuken en tradities (of
    // een lid met een eigen profiel). Opt-in: nooit ineens erbij bij een gezin zonder die keuzes.
    sohbet: { faiths: ['islam'], optIn: true, cultures: ['turkish'] },
};
/**
 * Eetvlaggen per pictogram-id: ALLEEN in eetcontext (snelle boodschappen, boodschappen- en
 * voorraadkiezer, recepten, weekmenu). Een ei bij "Paaseieren zoeken", een vis bij "Vissen" of
 * een kip bij "Kinderboerderij" blijven altijd zichtbaar. Alleen ingrediënten, nooit gerechten
 * (een "lasagne"-pictogram kan vega zijn).
 *
 * GEREVIEWD (builder B, 0024): ELK pictogram uit de groepen `food` en `groceries` (ui-core
 * art.ts) staat hier, ook als het niets bevat (`{}` = bekeken, past bij iedereen); de test in
 * catalog.test.ts bewaakt dat een nieuw eetpictogram niet ongelabeld blijft. Producten die meestal
 * iets bevatten maar niet altijd (koekjes, chocola, ijs), krijgen `maybe`: nooit verbergen,
 * hooguit een "let op".
 */
export const PICTO_FOOD_FLAGS = {
    // Groep food (maaltijden en gerechten: geen vlaggen, een gerecht kan alles zijn).
    breakfast: {},
    lunch: {},
    dinner: {},
    fruit: {},
    drink: {},
    cooking: {},
    baking: {},
    groceries: {},
    'ice-cream': { maybe: ['dairy', 'egg'] },
    popcorn: {},
    pizza: {},
    pancakes: {},
    soup: {},
    'oven-dish': {},
    stamppot: {},
    lasagne: {},
    curry: {},
    noodles: {},
    taco: {},
    wrap: {},
    salad: {},
    hamburger: {},
    fries: {},
    dumplings: {},
    waffle: {},
    pie: {},
    cake: {},
    pudding: {},
    // Groep groceries (ingrediënten en producten).
    banana: {},
    pear: {},
    orange: {},
    lemon: {},
    strawberry: {},
    grapes: {},
    tomato: {},
    cucumber: {},
    carrot: {},
    broccoli: {},
    lettuce: {},
    'bell-pepper': {},
    onion: {},
    garlic: {},
    potato: {},
    mushroom: {},
    corn: {},
    avocado: {},
    cheese: { flags: ['dairy'] },
    butter: { flags: ['dairy'] },
    egg: { flags: ['egg'] },
    yoghurt: { flags: ['dairy'] },
    croissant: { flags: ['dairy'], maybe: ['egg'] },
    honey: { flags: ['honey'] },
    meat: { flags: ['meat'], maybe: ['beef'] },
    chicken: { flags: ['meat'] },
    bacon: { flags: ['pork', 'meat'] },
    fish: { flags: ['fish'] },
    shrimp: { flags: ['shellfish'] },
    sausage: { flags: ['meat'], maybe: ['pork'] },
    rice: {},
    salt: {},
    'canned-food': {},
    peanuts: {},
    chocolate: { maybe: ['dairy'] },
    cookie: { maybe: ['dairy', 'egg'] },
    jar: {},
    coffee: {},
    tea: {},
    juice: {},
    water: {},
    'toilet-paper': {},
    soap: {},
    sponge: {},
    basket: {},
    'baby-bottle': {},
    receipt: {},
    store: {},
};
/** Labels van een pictogram in een context (zonder labels: past bij iedereen). */
export function pictoTags(id, context) {
    if (!id)
        return {};
    const faith = PICTO_FAITH_TAGS[id];
    const food = context === 'food' ? PICTO_FOOD_FLAGS[id] : undefined;
    if (!faith && !food)
        return {};
    return mergeTags(faith, food);
}
/**
 * Labels per ingebouwd sjabloon-id (0020_faith_templates). Builder B voegt de sjablonen voor
 * Koranles, Kerk, Sjabbat, Synagoge en Mandir toe (0024, opt-in) en labelt ze hier.
 */
export const BUILTIN_TEMPLATE_TAGS = {
    // event_template Moskeeles
    '01920000-0000-7000-8000-00000000000b': { faiths: ['islam'], practice: 'mosque' },
    // event_template Bidden
    '01920000-0000-7000-8000-00000000000c': { faiths: ALL_FAITHS, practice: 'prayer' },
    // event_template en task_template Wudu doen (0030_snel): alleen bij Islam, zoals Moskeeles.
    [BUILTIN_WUDU_IDS.event]: { faiths: ['islam'], practice: 'wudu' },
    [BUILTIN_WUDU_IDS.task]: { faiths: ['islam'], practice: 'wudu' },
    // task_template Bidden (Klusjes-ideeën)
    '0192a000-0000-7000-8000-000000000026': { faiths: ALL_FAITHS, practice: 'prayer' },
    // 0024_keukens_en_labels: NIEUWE geloofssjablonen, opt-in (pas zichtbaar als het gezin dat
    // geloof koos; een huishouden zonder profiel krijgt er niets bij).
    // event_template Koranles
    [BUILTIN_TEMPLATE_IDS.quran]: { faiths: ['islam'], optIn: true, practice: 'quran' },
    // event_template Kerk
    [BUILTIN_TEMPLATE_IDS.church]: { faiths: ['christianity'], optIn: true, practice: 'church' },
    // event_template Sjabbat
    [BUILTIN_TEMPLATE_IDS.shabbat]: { faiths: ['judaism'], optIn: true, practice: 'shabbat' },
    // event_template Synagoge
    [BUILTIN_TEMPLATE_IDS.synagogue]: { faiths: ['judaism'], optIn: true, practice: 'synagogue' },
    // event_template Mandir
    [BUILTIN_TEMPLATE_IDS.mandir]: { faiths: ['hinduism'], optIn: true, practice: 'mandir' },
};
/**
 * Labels van een boodschap (snelle keuze, boodschappen- of voorraadregel) in eetcontext: het
 * pictogram plus wat de naam zegt ("Kip", "Rookworst", "Halal rookworst").
 */
export function groceryTags(name, icon) {
    const found = detectIngredientFlags(name);
    const faith = icon ? PICTO_FAITH_TAGS[icon] : undefined;
    // De naam wint als hij iets zegt: "Kipspekjes" met het spekpictogram is geen varken. Zegt de
    // naam niets ("Beleg"), dan telt het pictogram.
    if (found.flags.length > 0 || found.maybe.length > 0) {
        return mergeTags(faith, { flags: found.flags, maybe: found.maybe });
    }
    return pictoTags(icon, 'food');
}
/**
 * Labels van een INGEBOUWD afspraak- of klusjessjabloon: die van het sjabloon zelf plus de
 * geloofslabels van zijn pictogram (context 'general': geen eetvlaggen). Een eigen sjabloon van
 * het gezin (household_id gezet) krijgt geen labels: dat verbergen we nooit.
 */
export function builtinTemplateTags(template) {
    if (!template.builtin)
        return {};
    return mergeTags(BUILTIN_TEMPLATE_TAGS[template.id], pictoTags(template.icon, 'general'));
}
//# sourceMappingURL=catalog.js.map